import React, { memo, useEffect, useRef, useMemo, useState, useContext } from 'react';
import {useDispatch, useSelector} from "react-redux";
import Split from "react-split";
import capitalize from 'lodash/capitalize';
import {Drawer} from '@mui/material';
import { styled } from '@mui/material/styles';
import HelpTree from './HelpTree';
import HelpDocument from './HelpDoc';
import {treeActions} from "../../redux/slices/treeSlice";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { api } from '../../redux/api';
import { lgDocPath } from '../../constants/languageDocPaths';
import AiHelpChatBot from './AiHelpChatBot';


export const anchorPositions = {
  top: 'top',
  left: 'left',
  bottom: 'bottom',
  right: 'right'
}

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '100%',
    backgroundColor: 'transparent'
  },
}));

var nodeMapper = {};

function HelpPanel() {
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const [helpTreeDrawer, setHelpTreeDrawer] = useState(false);
  const [enableSearchTree, setEnableSearchTree] = useState(false);
  const {showHelpPanel, currentScreen, helpDocList} = useSelector((state)=>state.treeData ?? {});
  const { rightTree } = useSelector(state => state.treeData?.[currentScreen] ?? {});
  const queryParams = useSelector(state => state.queryParams);
  const {currentTab, tree} = rightTree;
  const FILE_TYPE = tree?.templateHeader?.FILETYPE;
  const selectedNode = tree[currentTab]?.selectedNode;
  const [selectedHelpNode, setSelectedHelpNode] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [highlightKeyword, setHighlightKeyword] = useState('');
  const [searchDoc, setSearchDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const noNodeSelectedFromTree = Object?.keys(selectedNode || {})?.length === 0;
  const { account} = useSelector(state =>state.queryParams)
  const [aiHelpOpen, setAiHelpOpen] = useState(false);
  const noNodeSelected = 
  noNodeSelectedFromTree &&
  !selectedHelpNode;
  // a unique query parameter to the iframe's src attribute to prevent caching
  const timestamp = Date.now();

  const onClose = () => {
    setSelectedHelpNode(false);
    setSearchDoc(null);
    setEnableSearchTree(false);
    setExpandedKeys([]);
    setHighlightKeyword('');
    setAiHelpOpen(false);
    dispatch(treeActions.setShowHelpPanel(false));
    dispatch(api.endpoints.getHelpTreeList.initiate({account:account},{forceRefetch: true}))
  }

  //We either use this or do it in a better React way
  // const rightTreeWidth = document.getElementById('rightTreePanel') ? document.getElementById('rightTreePanel').offsetWidth : 0;
  const rightTreeWidth= 0;
  const rootWidth = document.getElementById('root') ? document.getElementById('root').offsetWidth : 0;
  let helpPanelWidth = (100 - ((rightTreeWidth + 8) / rootWidth * 100)).toFixed(3);
  helpPanelWidth = helpPanelWidth > 50 ? Math.ceil(helpPanelWidth) : 50;

  const documents = useMemo(() => 
  enableSearchTree ? 
  helpDocList : 
  [{
    title: 'Documentation',
    type: 'dir',
    children: helpDocList,
    root: true, 
   }], [enableSearchTree, helpDocList]);

  const rcTreeHelp = useMemo(() => {
    const data = 
    documents.map((doc, pIndex) => (rcHelpTransformer(doc, `ht-${pIndex}`, queryParams.baseUrl, queryParams.lang)));
    return data;
  },[documents, enableSearchTree, queryParams.baseUrl, queryParams.lang])

  const nodes = useMemo(() => {
    const recordAndFieldInbound = {
      RECORD_TOP: {
        name: 'record',
        node: nodeMapper[`${currentScreen}_jsonobject.html`],
      },
      RECORD: {
        name: 'record',
        node: nodeMapper[`${currentScreen}_record.html`],
      },
      FIELD_GROUP: {
        name: 'fieldgroup',
        node: nodeMapper[`${currentScreen}_fieldGroup.html`],
      },
      FIELD:{
        name: 'field',
        node: nodeMapper[`${currentScreen}_field.html`]
      },
      COMMENT: {
        name: 'comment',
        node: nodeMapper[`${currentScreen}_comment.html`]
      }
    };

    const recordAndFieldOutbound = {
      RECORD_TOP: {
        name: 'record',
        node: nodeMapper[`${currentScreen}_jsonobject.html`],
      },
      RECORD: {
        name: 'record',
        node: nodeMapper[`${currentScreen}_loop.html`],
      },
      RECORD_LOOP: {
        name: 'record',
        node: nodeMapper[`${currentScreen}_loop.html`],
      },
      FIELD_GROUP: {
        name: 'fieldgroup',
        node: nodeMapper[`${currentScreen}_fieldGroup.html`],
      },
      FIELD:{
        name: 'field',
        node: nodeMapper[`${currentScreen}_field.html`]
      },
      WARNING:{
        name: 'field',
        node: nodeMapper[`${currentScreen}_field.html`]
      },
      COMMENT: {
        name: 'comment',
        node: nodeMapper[`${currentScreen}_comment.html`]
      },
      VARIABLE : {
        name: 'variable',
        node: nodeMapper[`${currentScreen}_variable.html`]
      },
      QUERY : {
        name: 'customDBField',
        node: nodeMapper[`${currentScreen}_customdbfield.html`]
      },
      SEQUENCE : {
        name: 'sequence',
        node: nodeMapper[`${currentScreen}_sequence.html`]
      },
      VARIABLE_COLLECTION : {
        name: 'variableCollection',
        node: nodeMapper[`${currentScreen}_variablecollection.html`]
      },
      PLUGIN : {
        name: 'plugin',
        node: nodeMapper[`${currentScreen}_plugin.html`]
      },
      ACTION_ITEM : {
        name: 'action',
        node: nodeMapper[`${currentScreen}_action.html`]
      },
      TRACKING_REPORT_ITEM : {
        name: 'tracingReport',
        node: nodeMapper[`${currentScreen}_trackingreport.html`]
      },
      CONDITION_IF : {
        name:'conditionIf',
        node: nodeMapper[`${currentScreen}_conditionalfields.html`]
      },
      CONDITION_ELSE: {
        name: 'conditionElse',
        node: nodeMapper[`${currentScreen}_conditionalfieldsElse.html`]
      }
    };

    const currentNodes = {
      "inbound" : recordAndFieldInbound,
      "outbound" : recordAndFieldOutbound
    }[currentScreen];

    // For workflow screen, return direct mappings without file type layer
    if (currentScreen === "workflow") {
      return {
        OBJECTS: {
          name: 'workflow',
          node: nodeMapper[`workflow_index.html`]
        },
        GET_DATA: {
          name: 'getData',
          node: nodeMapper['getdata.html']
        },
        SEND_DATA: {
          name: 'sendData',
          node: nodeMapper['senddata.html']
        },
        TRANSLATE: {
          name: 'translate',
          node: nodeMapper['translate.html']
        },
        CALL_WORKFLOW: {
          name: 'callWorkflow',
          node: nodeMapper['callworkflow.html']
        },
        LOOP_ITEM: {
          name: 'loopItem',
          node: nodeMapper['loopItem.html']
        },
        CONDITION_IF: {
          name: 'conditionIf',
          node: nodeMapper['itemlist.html']  // Conditional fields are part of item list
        },
        CONDITION_ELSE: {
          name: 'conditionElse',
          node: nodeMapper['else_wf_item.html']
        },
        ACTION: {
          name: 'action',
          node: nodeMapper['action.html']
        },
        ACTION_ITEM: {
          name: 'action',
          node: nodeMapper['action.html']
        },
        STOP_ACTION: {
          name: 'action',
          node: nodeMapper['action.html']
        },
        ERROR_HANDLER: {
          name: 'errorHandler',
          node: nodeMapper['error.html']
        },
        TRACKING_REPORT_ITEM: {
          name: 'trackingReport',
          node: nodeMapper['trackingreport.html']
        },
        VARIABLE_ITEM: {
          name: 'variable',
          node: nodeMapper['variable.html']
        },
        CALL_PLUGIN: {
          name: 'callPlugin',
          node: nodeMapper['callplugin.html']
        },
        TRADING_PARTNER_LOOKUP: {
          name: 'tradingPartnerLookup',
          node: nodeMapper['trading_partner_lookup.html']
        },
        VARIABLE: {
          name: 'variable',
          node: nodeMapper['variable.html']
        },
        PLUGIN: {
          name: 'plugin',
          node: nodeMapper['callplugin.html']
        },
        COMMENT: {
          name: 'comment',
          node: nodeMapper['comment.html']
        },
        custom: {
          REGULAR_EXPRESSION:{
            name: 'regex',
            node: nodeMapper['regex.html']
          },
          ACCELERATOR: {
            name: 'accelerator',
            node: nodeMapper['accelerators.html']
          }
        },
        default: nodeMapper['index.html']
      };
    }

    // For inbound/outbound screens, return the file type structure
    return {
      CSV: currentNodes,
      DELIM_FLD_ID: currentNodes,
      DELIMITED: currentNodes,
      MULIT_DELIMITED: currentNodes,
      FIXED_LENGTH: currentNodes,
      EXCEL: currentNodes,
      HL7: currentNodes,
      EDI: currentNodes,
      SERVLET: currentNodes,
      NVP: currentNodes,
      XML: {
        ELEMENT: {
          name: 'element',
          node: nodeMapper[`${currentScreen}_element.html`],
        },
        ATTRIBUTE:{
          name: 'attribute',
          node: nodeMapper[`${currentScreen}_attribute.html`],
        },
        ...currentNodes
      },
      DATABASE: {
        QUERY: {
          name: 'database record',
          node: nodeMapper[`${currentScreen}_databaserecord.html`],
        },
        DATABASE_FIELD: {
          name: 'database field',
          node: nodeMapper[`${currentScreen}_databasefield.html`],
        },
        ...currentNodes
      },
      JSON: {
        RECORD_TOP: {
          name: 'record',
          node: nodeMapper[`${currentScreen}_jsonobject.html`],
        },
        RECORD: {
          name: 'record',
          node: nodeMapper[`${currentScreen}_jsonobject.html`],
        },
        FIELD:{
          name: 'field',
          node: nodeMapper[`${currentScreen}_jsonfield.html`]
        },
        FIELD_LOOP:{
          name: 'field',
          node: nodeMapper[`${currentScreen}_jsonfield.html`]
        },
        ...currentNodes
      },
      custom: {
        REGULAR_EXPRESSION:{
          name: 'regex',
          node: nodeMapper[`regex.html`]
        },
        ACCELERATOR: {
          name: 'accelerator',
          node: nodeMapper[`accelerators.html`]
        }
      },
      GraphQL: {
        RECORD:{
          name: 'graphqlRecord',
          node: nodeMapper[`${currentScreen}_graphqlloop.html`]
        }
      },
      default: nodeMapper[`${currentScreen}_index.html`]
    }
  }, [rcTreeHelp, nodeMapper, currentScreen]);

  const {docNodeKey, docPath, docExpandedKeys} = useMemo(() => {
    const customNodesObj = nodes?.custom;
    const customHelp = customNodesObj?.[showHelpPanel?.docType];
    if(customHelp)
      return {
        docNodeKey: [customHelp?.node?.key],
        docPath: customHelp?.node?.filePath,
        docExpandedKeys: getExpandedKeys(customHelp?.node?.key || ""),
      };
    if(selectedHelpNode) 
      return {
          docNodeKey: [selectedHelpNode?.key],
          docPath: selectedHelpNode?.filePath,
          docExpandedKeys: getExpandedKeys(selectedHelpNode?.key || "")
      };
    // For workflow screen, look directly at ICON_DISPLAY (no file type layer)
    if (currentScreen === "workflow") {
      const findNode = nodes?.[selectedNode?.ICON_DISPLAY];
      if(findNode)
        return {
          docNodeKey: [findNode?.node?.key],
          docPath: findNode?.node?.filePath,
          docExpandedKeys: getExpandedKeys(findNode?.node?.key || ""),
        };
    } else {
      // For inbound/outbound screens, use file type then ICON_DISPLAY
      const nodeFile = nodes?.[FILE_TYPE];
      const findNode = nodeFile?.[selectedNode?.ICON_DISPLAY];
      if(findNode)
        return {
          docNodeKey: [findNode?.node?.key],
          docPath: findNode?.node?.filePath,
          docExpandedKeys: getExpandedKeys(findNode?.node?.key || ""),
        };
    }
    if(selectedNode.ICON_DISPLAY === "TEMPLATE_BODY_DOTS"){
      const defaultNode = nodes.default;
      return {
        docNodeKey: [defaultNode?.key],
        docPath: defaultNode?.filePath,
        docExpandedKeys: getExpandedKeys(defaultNode?.key || ""),
      };
    }
    return {};
  }, [selectedNode, selectedHelpNode, FILE_TYPE, nodes , showHelpPanel?.docType]);


  useEffect(() => {
    if(enableSearchTree && rcTreeHelp.length > 0) {
      setExpandedKeys(rcTreeHelp.map((val) => val.key));
      
      // Auto-select the first document node in search results
      const firstCategory = rcTreeHelp[0];
      if(firstCategory?.children?.length > 0) {
        const firstDoc = firstCategory.children[0];
        setSelectedHelpNode(firstDoc);
        
        if(highlightKeyword && firstDoc?.path) {
          dispatch(
            api.endpoints.searchHelpDoc.initiate({ 
              keyword: highlightKeyword, 
              searchString: firstDoc.path 
            }, {forceRefetch:true})
          ).then((response) => {
            if(response?.isSuccess){
              setSearchDoc(response?.data?.documentation);
            }
          });
        }
      }
    }
    else {
      setExpandedKeys([]);
    }
  },[enableSearchTree, rcTreeHelp])

  useEffect(() => {
    setTimeout(() => {
      if (typeof showHelpPanel !== "boolean") {

      }
      if(docNodeKey?.[0] && showHelpPanel && selectedNode && !selectedHelpNode){
        let nodeElement = document.getElementById(docNodeKey[0]);
        if (nodeElement) {
          nodeElement.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }, 500)
  },[docNodeKey, selectedNode, showHelpPanel, FILE_TYPE, nodes])

  

  const collectedKeys = collectKeysBySequence(rcTreeHelp);
  const collectedNodes = collectFlatNodesFromTree(rcTreeHelp);
  return (
    <StyledDrawer
      anchor={anchorPositions.left}
      open={showHelpPanel || showHelpPanel?.isOpen}
      onClose={onClose}
    >
      <Split
        className="split height-100 flex"
        direction="horizontal"
        gutterSize={4}
        snapOffset={0}
        dragInterval={4}
        minSize={[500, 0]}
        sizes={[helpPanelWidth, 100 - helpPanelWidth]}
      >
        <div className="flex height-100" style={{ backgroundColor: "#fff" }}>
          <Split
            className="split height-100 width-100 flex"
            direction="horizontal"
            gutterSize={4}
            snapOffset={0}
            dragInterval={4}
            minSize={[40, 300]}
            sizes={[20, 80]}
          >
            <HelpTree
              focusable
              className="myCls"
              showLine
              autoFocus
              treeData={rcTreeHelp}
              enableSearchTree={enableSearchTree}
              setEnableSearchTree={setEnableSearchTree}
              highlightKeyword={highlightKeyword}
              setHighlightKeyword={setHighlightKeyword}
              setSelectedNode={setSelectedHelpNode}
              selectedKeys={docNodeKey}
              expandedKeys={expandedKeys?.length > 0 ? expandedKeys : docExpandedKeys}
              setExpandedKeys={setExpandedKeys}
              setSearchDocument={setSearchDoc}
              selectable
              baseUrl={queryParams.baseUrl}
              lang={queryParams.lang}
              loading={loading}
              setLoading={setLoading}
              collectedKeys={collectedKeys}
              collectedNodes={collectedNodes}
              aiHelpOpen={aiHelpOpen}
              setAiHelpOpen={setAiHelpOpen}
            />

            <div style={{padding: "6px", flexGrow: '1'}}>
              {(!aiHelpOpen) && <HelpDocument
                onClose={onClose}
                helpTree={helpTreeDrawer}
                onHelpTree={setHelpTreeDrawer}
                selectedNode={selectedHelpNode || selectedNode}
                isCustomHelp={showHelpPanel?.docPath}
              >
                {(enableSearchTree && searchDoc) ?
                <iframe
                className="docFrame"
                src={`${queryParams.baseUrl}/docs/${lgDocPath[queryParams.lang] || 'en'}${searchDoc}?timestamp=${timestamp}`}
                title="Help Documentation"
                /> :
                docPath ? (
                  <iframe
                    className="docFrame"
                    src={`${docPath}?timestamp=${timestamp}`}
                    title="Help Documentation"
                  ></iframe>
                ) : 
                noNodeSelected ? 
                <iframe
                className="docFrame"
                src={`${queryParams.baseUrl}/docs/${lgDocPath[queryParams.lang] || 'en'}/Workbench/bridgegate_health_index.html?timestamp=${timestamp}`}
                title="Help Documentation"
                />:
                (
                
                  <div style={{border: "1px solid #e0e0e0",}} className="docFrame panel-no-data">
                    Select a help document to display
                  </div>
                )}
              </HelpDocument>}
              {aiHelpOpen && <AiHelpChatBot onClose= {onClose}/>}
            </div>
          </Split>
        </div>
        <div onClick={onClose}></div>
      </Split>
    </StyledDrawer>
  ); 
}

const getExpandedKeys = (key) => {
  const splitKeys = key.split("-");
  const expandedKeys = splitKeys.reduce((acc, cur, index) => {
    const lastKey = acc[index - 1];
    const newKey = lastKey ? `${lastKey}-${cur}` : cur;
    acc.push(newKey);
    return acc;
  }, []);
  expandedKeys.shift();
  return expandedKeys;
}

const collectKeysBySequence = (treeData, keys = []) => {
  if(treeData?.type === "file")
    return [treeData?.key];
  const data = (Array.isArray(treeData) ? treeData : treeData?.children)?.reduce((acc, cur) => {
    const val = collectKeysBySequence(cur, [cur?.key]);
    return [...acc, ...val];
  }, [])
  return [...keys, ...data];
}

const getFileLabel = (file) => 
file.replace(".html","").split("_").map((v) => capitalize(v)).join(" ");

const rcHelpTransformer = (doc, pExp, baseUrl, lang = "en", filePath = null, preparePath = null) => {
  if(doc.type === "dir"){
    const children = doc.children.map((dc, sId) => {
      if(dc.type === "dir")
      return rcHelpTransformer(
        dc, 
        `${pExp}-${sId}`, 
        baseUrl, 
        lang, 
        doc.root ?
        filePath :
        filePath ?
        filePath + "/" + dc.title :
        `/${doc.title}/${dc.title}`,
        doc.root ?
        preparePath : 
        preparePath ? 
        preparePath + "/" + dc.title :
        `${baseUrl}/docs/${lgDocPath[lang] || 'en'}/${doc.title}/${dc.title}`);
      let prepareNode = {
        key: `${pExp}-${sId}`,
        title: getFileLabel(dc.title),
        type: dc.type,
        fileName: dc.title,
        path: filePath ?
        filePath + "/" + dc.title :
        `/${doc.title}/${dc.title}`,
        filePath: preparePath ? 
        preparePath + "/" + dc.title : 
        `${baseUrl}/docs/${lgDocPath[lang] || 'en'}/${doc.title}/${dc.title}`
      };
      if(doc.title === "inbound" || doc.title === "outbound"){
        nodeMapper[`${doc.title}_${dc.title}`]= prepareNode
      }
      else{
        if (doc.title  === "workflow" && dc.title ===  "index.html") nodeMapper[`${doc.title}_${dc.title}`]= prepareNode
        else nodeMapper[dc.title]= prepareNode
      }
      return prepareNode; 
    })  
    return {
      key: pExp,
      title: doc.title,
      type: doc.type,
      children
    }
  }
  let prepareNode = {
    key: pExp,
    title: getFileLabel(doc.title),
    type: doc.type,
    fileName: doc.title,
    path: filePath ?
    filePath + "/" + doc.title :
    `/${doc.title}`,
    filePath: preparePath ? 
    preparePath + "/" + doc.title :
    `${baseUrl}/docs/${lgDocPath[lang] || 'en'}/${doc.title}`
  }
  nodeMapper[doc.title]= prepareNode
  return prepareNode
}

const collectFlatNodesFromTree = (treeData, nodes = {}) => {
  if(treeData?.type === "file")
    return {[treeData.key]: treeData};
  const data = (Array.isArray(treeData) ? treeData : treeData?.children)?.reduce((acc, cur) => {
    const val = collectFlatNodesFromTree(cur, {[cur.key]: cur});
    return {...acc, ...val};
  }, {})
  return {...nodes, ...data};
}


export default memo(HelpPanel);